// Lazy Shiki highlighter, trimmed for YesQL from desktop-ui's shared
// `lib/shiki.ts`: standalone YesQL only ever highlights SQL and has no
// desktop-wide theme picker, so exactly one grammar (sql) and one theme
// (github-dark) are bundled. The public `highlightSnippet` signature is kept
// identical so the ported components stay 1:1 with the source.

import type { HighlighterCore } from 'shiki/core';

export const SHIKI_THEME = 'github-dark';

let highlighterPromise: Promise<HighlighterCore> | null = null;

async function getHighlighter(): Promise<HighlighterCore> {
  if (!highlighterPromise) {
    highlighterPromise = (async () => {
      const [{ createHighlighterCore }, { createJavaScriptRegexEngine }, theme, sql] =
        await Promise.all([
          import('shiki/core'),
          import('shiki/engine/javascript'),
          import('shiki/themes/github-dark.mjs'),
          import('shiki/langs/sql.mjs'),
        ]);
      return createHighlighterCore({
        themes: [theme.default as never],
        langs: [sql.default],
        engine: createJavaScriptRegexEngine(),
      });
    })();
  }
  return highlighterPromise;
}

// Render a single code snippet to HTML. Returns the inner HTML of Shiki's
// <code> element (the colored <span>s only) — callers splice this into
// their existing <pre><code> wrapper to preserve app-level styling (bg,
// copy button, border radius) instead of Shiki's inline-styled <pre>.
//
// `lang` and `themeName` are accepted for signature compatibility with the
// desktop-ui original; anything other than SQL is refused (returns null so
// callers fall back to plain text).
export async function highlightSnippet(
  code: string,
  lang: string,
  _themeName: string,
): Promise<string | null> {
  if (lang.toLowerCase() !== 'sql') return null;
  const highlighter = await getHighlighter();
  const html = highlighter.codeToHtml(code, {
    lang: 'sql',
    theme: SHIKI_THEME,
  });
  // Pull the innerHTML of the <code> element. Shiki's output is stable
  // and trusted (known span classes + inline color styles); we discard
  // the wrapping <pre> so our own styles survive.
  const match = html.match(/<code[^>]*>([\s\S]*)<\/code>/);
  return match?.[1] ?? null;
}
